import { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
ChartJS.register(ChartDataLabels);

function Barchart({ product_distribution, type, filter }) {
    const { t } = useTranslation();
    const [barChartData, setBarChartData] = useState(null);
    const [barChartOptions, setBarChartOptions] = useState(null);

    useEffect(() => {
        if (!product_distribution) return;

        var key = type === "sales" ? "total_sale_cnt_rate" : "total_gmv_amt_rate";

        const sorted = _
            .chain(product_distribution)
            .uniqBy("type")
            .orderBy(item => parseFloat(item[key]), "desc")
            .value();

        const barLabels = sorted.map(item => item.type);
        const barData = sorted.map(item => parseFloat(item[key]));
        const backgroundColors = barLabels.map((_, i) => `hsl(${i * 60}, 70%, 60%, 0.8)`);

        setBarChartData({
            labels: barLabels,
            datasets: [
                {
                    label: "% Sales",
                    data: barData,
                    backgroundColor: backgroundColors,
                },
            ],
        });

        setBarChartOptions({
            indexAxis: "y",
            responsive: true,
            plugins: {
                legend: {
                    display: true,
                    position: "top",
                    labels: {
                        generateLabels(chart) {
                            const dataset = chart.data.datasets[0];
                            const meta = chart.getDatasetMeta(0);

                            return chart.data.labels.map((label, i) => ({
                                text: label,
                                fillStyle: dataset.backgroundColor[i],
                                strokeStyle: dataset.backgroundColor[i],
                                lineWidth: 0,
                                index: i,
                                pointStyle: "circle",
                                datasetIndex: 0,
                                hidden: meta.data[i] ? meta.data[i].hidden : false,
                            }));
                        },
                        usePointStyle: true,
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                    onClick(e, legendItem, legend) {
                        const chart = legend.chart;
                        const datasetIndex = legendItem.datasetIndex;
                        const labelIndex = legendItem.index;

                        const meta = chart.getDatasetMeta(datasetIndex);
                        const bar = meta.data[labelIndex];
                        if (!bar) return;

                        bar.hidden = !bar.hidden;

                        if (!chart.config.options.plugins.datalabels.display || !Array.isArray(chart.config.options.plugins.datalabels.display)) {
                            chart.config.options.plugins.datalabels.display = Array(chart.data.labels.length).fill(true);
                        }
                        chart.config.options.plugins.datalabels.display[labelIndex] = !bar.hidden;

                        const dataset = chart.data.datasets[datasetIndex];
                        const visibleValues = dataset.data.filter((_, i) => {
                            const meta = chart.getDatasetMeta(datasetIndex);
                            const bar = meta.data[i];
                            return !bar.hidden;
                        });

                        const total = visibleValues.reduce((sum, val) => sum + val, 0);
                        chart.config.options.scales.x.max = Math.min(100, Math.ceil(total));

                        chart.update();
                    }
                },
                title: {
                    display: true,
                    text: `${t("Product Category Distribution")} (${(filter.dateRange || "30") + " days"})`,
                },
                tooltip: {
                    displayColors: false,
                    callbacks: {
                        label: (ctx) => {
                            const index = ctx.dataIndex;
                            return [
                                `Percentage of GMV: ${sorted[index][key]}`,
                                `GMV: ${sorted[index].total_sale_cnt}`,
                                `No. of Product: ${sorted[index].total_product_cnt}`,
                            ];
                        },
                    },
                },
                datalabels: {
                    anchor: "end",
                    align: "right",
                    formatter: (value) => `${value.toFixed(2)}%`,
                    color: "#000",
                    display: (ctx) => {
                        const displayArr = ctx.chart?.config?.options?.plugins?.datalabels?.display;
                        return Array.isArray(displayArr) ? displayArr[ctx.dataIndex] !== false : true;
                    },
                },
            },
            scales: {
                x: {
                    ticks: {
                        callback: (value) => `${value}%`,
                    },
                    beginAtZero: true,
                    max: 100,
                },
            },
        });
    }, [product_distribution, type, filter, t]);

    return (
        <Fragment>
            {barChartData && barChartOptions && (
                <Bar data={barChartData} options={barChartOptions} />
            )}
        </Fragment>
    );
}

const mapStateToProps = state => ({
});
const mapDispatchToProps = {
}

export default connect(mapStateToProps, mapDispatchToProps)(Barchart);